<template>
  <nav class="intercom-sidebar">
    <div
      v-for="section in navSections"
      :key="section.label"
      class="intercom-sidebar-section"
    >
      <div v-if="section.label" class="intercom-sidebar-section-label">
        {{ section.label }}
      </div>

      <router-link
        v-for="item in section.items"
        :key="item.path"
        :to="item.path"
        class="intercom-sidebar-item"
        :class="{ 'intercom-sidebar-item--active': isActive(item.path) }"
      >
        <span class="intercom-sidebar-item-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            v-html="item.icon"
          />
        </span>
        <span>{{ item.label }}</span>
      </router-link>
    </div>
  </nav>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
  /**
   * Navigation sections
   * @example [{ label: 'Main', items: [{ label: 'Dashboard', path: '/', icon: '...' }] }]
   */
  navSections: {
    type: Array,
    required: true,
    validator: (value) => {
      return value.every(
        (section) =>
          section.items &&
          Array.isArray(section.items) &&
          section.items.every((item) => item.label && item.path)
      )
    },
  },
})

const route = useRoute()

const allPaths = computed(() =>
  props.navSections.flatMap(s => s.items.map(i => i.path))
)

const isActive = (path) => {
  // Exact match for home
  if (path === '/') {
    return route.path === '/'
  }
  // Check if current path starts with this nav path
  if (!route.path.startsWith(path)) {
    return false
  }
  // If a more specific (longer) sibling path also matches, don't highlight this one
  const hasMoreSpecificMatch = allPaths.value.some(
    other => other !== path && other.length > path.length && route.path.startsWith(other)
  )
  return !hasMoreSpecificMatch
}
</script>

<style scoped>
/* ========================================
   INTERCOM SIDEBAR BASE
   ======================================== */
.intercom-sidebar {
  padding: var(--space-4);
  height: 100%;
  overflow-y: auto;
}

/* ========================================
   SECTIONS
   ======================================== */
.intercom-sidebar-section {
  margin-bottom: var(--space-6);
}

.intercom-sidebar-section:last-child {
  margin-bottom: 0;
}

.intercom-sidebar-section-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wider);
  color: var(--color-gray-500);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-1);
}

/* ========================================
   NAV ITEMS
   ======================================== */
.intercom-sidebar-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-gray-700);
  text-decoration: none;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast);
  margin-bottom: var(--space-1);
}

.intercom-sidebar-item:hover {
  background: var(--color-gray-100);
  color: var(--color-charcoal);
}

.intercom-sidebar-item--active {
  background: var(--color-blue-light);
  color: var(--color-blue);
  font-weight: var(--font-weight-semibold);
}

.intercom-sidebar-item--active:hover {
  background: var(--color-blue-light);
  color: var(--color-blue);
}

.intercom-sidebar-item-icon {
  width: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.intercom-sidebar-item-icon svg {
  display: block;
}
</style>
